import { useState, useRef, useCallback, useEffect } from "react";
import {
  Upload,
  Download,
  CheckCircle,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  createImportJob,
  uploadImportFile,
  detectHeaders,
  executeImport,
  resetImportJob,
  deleteImportJob,
} from "@/services/import";
import type {
  UploadResult,
  DetectHeadersResult,
  ValidationResult,
  ImportExecuteResult,
} from "@/types/import";
import { MappingStep } from "./MappingStep";
import { PreviewStep } from "./PreviewStep";
import { SummaryStep } from "./SummaryStep";
import { buildTemplateCsv } from "./import-template";

type WizardStep = "upload" | "mapping" | "preview" | "summary";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

// CSV template with German Excel Mode (semicolon delimiter, UTF-8 BOM)
function downloadTemplate() {
  const csv = buildTemplateCsv();
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "quiznudge-import-vorlage.csv";
  a.click();
  URL.revokeObjectURL(url);
}

function isCSVFile(file: File): boolean {
  return (
    file.type === "text/csv" ||
    file.type === "application/vnd.ms-excel" ||
    file.name.endsWith(".csv")
  );
}

interface UploadSuccess {
  fileName: string;
  jobId: string;
  upload: UploadResult;
  headers: DetectHeadersResult;
}

const DELIMITER_LABELS: Record<string, string> = {
  ";": "Semikolon",
  ",": "Komma",
  "\t": "Tab",
  "|": "Pipe",
};

const STORAGE_KEY = "quiznudge-import-wizard-state";

interface PersistedWizardState {
  step: WizardStep;
  result: UploadSuccess | null;
  validationResult: ValidationResult | null;
}

function loadWizardState(): PersistedWizardState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PersistedWizardState;
  } catch {
    return null;
  }
}

function saveWizardState(state: PersistedWizardState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage full or unavailable
  }
}

function clearWizardState(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

export function ImportPage() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<UploadSuccess | null>(
    () => loadWizardState()?.result ?? null,
  );
  const [step, setStep] = useState<WizardStep>(
    () => loadWizardState()?.step ?? "upload",
  );
  const [validationResult, setValidationResult] =
    useState<ValidationResult | null>(
      () => loadWizardState()?.validationResult ?? null,
    );
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportExecuteResult | null>(
    null,
  );
  const [importError, setImportError] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);

  useEffect(() => {
    if (step === "summary") {
      clearWizardState();
      return;
    }
    if (step === "upload" && !result) return;
    saveWizardState({ step, result, validationResult });
  }, [step, result, validationResult]);

  const validateAndUpload = useCallback(async (file: File) => {
    setError(null);
    setResult(null);

    if (!isCSVFile(file)) {
      setError("Bitte wählen Sie eine CSV-Datei aus.");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError("Die Datei ist zu groß. Maximal 5 MB erlaubt.");
      return;
    }

    setIsUploading(true);
    try {
      const job = await createImportJob();
      const upload = await uploadImportFile(job.id, file);
      const headers = await detectHeaders(job.id);
      setResult({ fileName: file.name, jobId: job.id, upload, headers });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Beim Upload ist ein Fehler aufgetreten.",
      );
    } finally {
      setIsUploading(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) validateAndUpload(file);
    },
    [validateAndUpload],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) validateAndUpload(file);
      // Reset so the same file can be re-selected
      e.target.value = "";
    },
    [validateAndUpload],
  );

  const handleReset = () => {
    clearWizardState();
    setStep("upload");
    setResult(null);
    setValidationResult(null);
    setImportResult(null);
    setImportError(null);
    setIsImporting(false);
  };

  // Reset path: send the job back to 'uploaded' server-side, drop the stale
  // validation result locally, and return to the mapping step. parsed_rows and
  // field_mapping survive, so "Speichern & Weiter" works again without a 409.
  const handleBackToMapping = async () => {
    if (!result?.jobId || isResetting) return;
    setIsResetting(true);
    try {
      await resetImportJob(result.jobId);
    } catch {
      // A failed reset (e.g. 409 because the job is already 'uploaded') still
      // means the user should land in mapping — never leave them in the trap.
    } finally {
      setValidationResult(null);
      setStep("mapping");
      setIsResetting(false);
    }
  };

  // Delete path: discard the job entirely and restart from the dropzone.
  const handleDiscardAndRestart = async () => {
    if (!result?.jobId || isDiscarding) return;
    setIsDiscarding(true);
    try {
      await deleteImportJob(result.jobId);
    } catch {
      // 404 (job already gone) or any other failure — clean up locally anyway.
    } finally {
      handleReset();
      setIsDiscarding(false);
    }
  };

  if (step === "summary" && importResult && result) {
    return (
      <div className="p-8">
        <h1 className="text-2xl font-bold">Produkte importieren</h1>
        <p className="mt-2 text-muted-foreground">Ergebnis des Imports.</p>
        <div className="mt-8">
          <SummaryStep
            jobId={result.jobId}
            status={importResult.status}
            processedRows={importResult.processedRows}
            successRows={importResult.successRows}
            errorRows={importResult.errorRows}
            skippedRows={importResult.skippedRows}
            importedTags={importResult.importedTags}
            fileName={result.fileName}
            onReset={handleReset}
          />
        </div>
      </div>
    );
  }

  if (step === "preview" && validationResult) {
    const handleExecute = async () => {
      if (!result?.jobId || isImporting || importResult) return;
      setIsImporting(true);
      setImportError(null);
      try {
        const execResult = await executeImport(result.jobId);
        setImportResult(execResult);
        setStep("summary");
      } catch (err) {
        setImportError(
          err instanceof Error
            ? err.message
            : "Beim Import ist ein Fehler aufgetreten.",
        );
      } finally {
        setIsImporting(false);
      }
    };

    return (
      <div className="p-8">
        <h1 className="text-2xl font-bold">Produkte importieren</h1>
        <p className="mt-2 text-muted-foreground">
          Prüfen Sie die Validierungsergebnisse vor dem Import.
        </p>
        <div className="mt-8">
          <PreviewStep
            validationResult={validationResult}
            onBackToMapping={handleBackToMapping}
            onDiscard={handleDiscardAndRestart}
            onExecute={handleExecute}
            isImporting={isImporting}
            isResetting={isResetting}
            isDiscarding={isDiscarding}
            importDone={!!importResult}
          />
          {importError && (
            <div className="mt-4 flex items-start gap-3 rounded-lg border border-destructive/50 bg-destructive/10 p-4">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
              <p className="text-sm text-destructive">{importError}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (step === "mapping" && result) {
    return (
      <div className="p-8">
        <h1 className="text-2xl font-bold">Produkte importieren</h1>
        <p className="mt-2 text-muted-foreground">
          Ordnen Sie die CSV-Spalten den QuizNudge-Feldern zu.
        </p>
        <div className="mt-8">
          <MappingStep
            jobId={result.jobId}
            headerResult={result.headers}
            onBack={() => setStep("upload")}
            onDiscard={handleDiscardAndRestart}
            isDiscarding={isDiscarding}
            onSaveAndValidate={(vr) => {
              setValidationResult(vr);
              setStep("preview");
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Page Header */}
      <h1 className="text-2xl font-bold">Produkte importieren</h1>
      <p className="mt-2 text-muted-foreground">
        Lade deine Produktdaten als CSV-Datei hoch. QuizNudge erkennt deutsche
        Excel-Exporte automatisch.
      </p>

      <div className="mt-8 space-y-6">
        {/* Template Download */}
        <Card>
          <CardContent className="flex items-center justify-between gap-4 py-4">
            <p className="text-sm text-muted-foreground">
              Unsere CSV-Vorlage enthält alle Pflichtfelder mit Beispieldaten.
            </p>
            <Button variant="outline" onClick={downloadTemplate}>
              <Download className="mr-2 h-4 w-4" />
              Vorlage herunterladen (.csv)
            </Button>
          </CardContent>
        </Card>

        {/* Upload Zone */}
        {!result && (
          <div
            role="button"
            tabIndex={0}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            className={cn(
              "flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-12 transition-colors",
              isDragOver
                ? "border-primary bg-primary/5"
                : "border-muted-foreground/25 hover:border-muted-foreground/50",
              isUploading && "pointer-events-none opacity-60",
            )}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleFileChange}
            />

            {isUploading ? (
              <>
                <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
                <p className="mt-4 text-sm font-medium">
                  Datei wird hochgeladen und analysiert…
                </p>
              </>
            ) : (
              <>
                <Upload className="h-10 w-10 text-muted-foreground" />
                <p className="mt-4 text-sm font-medium">
                  CSV-Datei hierher ziehen oder klicken zum Auswählen
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Max. 5 MB, CSV-Format
                </p>
              </>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-start gap-3 rounded-lg border border-destructive/50 bg-destructive/10 p-4">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Success */}
        {result && (
          <Card>
            <CardContent className="space-y-4 py-6">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <p className="text-sm font-medium">
                  {result.fileName} erfolgreich hochgeladen
                </p>
              </div>

              <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm text-muted-foreground">
                <span>Zeilen erkannt:</span>
                <span className="font-medium text-foreground">
                  {result.upload.rawRowCount}
                </span>
                <span>Spalten:</span>
                <span className="font-medium text-foreground">
                  {result.upload.detectedHeaders.length}
                </span>
                <span>Trennzeichen:</span>
                <span className="font-medium text-foreground">
                  {DELIMITER_LABELS[result.upload.detectedDelimiter] ??
                    result.upload.detectedDelimiter}
                </span>
                <span>Encoding:</span>
                <span className="font-medium text-foreground">
                  {result.upload.detectedEncoding}
                </span>
              </div>

              <div className="pt-2">
                <Button onClick={() => setStep("mapping")}>
                  Weiter zu Spalten-Zuordnung &rarr;
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
